import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { User, Lock, Warning, EnvelopeSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ROLE_UTILISATEUR, ROLE_PROJET, FONCTIONS } from '../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_LENGTH = 8;

// Libellés du vocabulaire unique (09-VOCABULAIRE §4)
const ROLE_LABELS = {
  ...ROLE_UTILISATEUR,
  moe_principal: ROLE_PROJET.moe_principal,
  moe_membre: ROLE_PROJET.moe_membre,
  consultant: ROLE_PROJET.consultant,
};

// RP07 — « Mon compte » pour tous les rôles : nom, téléphone, mot de passe.
export default function MonCompte() {
  const { user, checkAuth } = useAuth();
  const [profil, setProfil] = useState({ name: '', phone: '', sms: false });
  const [savingProfil, setSavingProfil] = useState(false);
  const [mdp, setMdp] = useState({ current: '', next: '', confirm: '' });
  const [savingMdp, setSavingMdp] = useState(false);
  const [erreurMdp, setErreurMdp] = useState('');
  // RP26-27 (D18) — entreprise : e-mails regroupés une fois par jour ou au fil de l'eau
  const [prefsEmails, setPrefsEmails] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/me/preferences-emails`, { withCredentials: true })
      .then(({ data }) => setPrefsEmails(data))
      .catch(() => setPrefsEmails(null));
  }, []);

  const choisirFrequence = async (frequence) => {
    setSavingPrefs(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/me/preferences-emails`, { frequence }, { withCredentials: true });
      setPrefsEmails(data);
      toast.success('Réglage des e-mails enregistré');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingPrefs(false);
    }
  };

  useEffect(() => {
    if (user) setProfil({ name: user.name || '', phone: user.phone || user.telephone || '', sms: !!user.sms_notifications_enabled });
  }, [user]);

  const saveProfil = async (e) => {
    e.preventDefault();
    if (!profil.name.trim()) { toast.error('Le nom ne peut pas être vide'); return; }
    setSavingProfil(true);
    try {
      await axios.put(`${API_URL}/api/users/profile`, { name: profil.name, phone: profil.phone, sms_notifications_enabled: profil.sms }, { withCredentials: true });
      toast.success('Informations enregistrées');
      checkAuth && checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingProfil(false);
    }
  };

  const saveMdp = async (e) => {
    e.preventDefault();
    setErreurMdp('');
    if (mdp.next.length < MIN_LENGTH) return setErreurMdp(`Le nouveau mot de passe doit contenir au moins ${MIN_LENGTH} caractères.`);
    if (mdp.next !== mdp.confirm) return setErreurMdp('Les deux nouveaux mots de passe ne correspondent pas.');
    setSavingMdp(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: mdp.current, new_password: mdp.next },
        { withCredentials: true },
      );
      toast.success('Mot de passe modifié');
      setMdp({ current: '', next: '', confirm: '' });
      checkAuth && checkAuth();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErreurMdp(typeof detail === 'string' ? detail : 'Modification impossible. Réessayez.');
    } finally {
      setSavingMdp(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-2xl" data-testid="mon-compte-page">
        <div className="mb-6">
          <h1 className="text-3xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Mon compte
          </h1>
          <p className="text-[#6B6B6B] mt-1">Vos informations et votre mot de passe</p>
        </div>

        {user?.must_change_password && (
          <div className="mb-6 flex items-start gap-2 bg-[#FDF6EC] border-l-4 border-[#C4773B] p-3 text-sm text-[#7A3B0A]" data-testid="mon-compte-conseil-mdp">
            <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
            <p>Votre mot de passe actuel vous a été communiqué par le cabinet. Choisissez-en un personnel ci-dessous.</p>
          </div>
        )}

        <form onSubmit={saveProfil} className="card-kroquis space-y-4 mb-6" data-testid="mon-compte-profil">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B] flex items-center gap-2">
            <User size={16} /> Mes informations
          </h2>
          <div>
            <Label htmlFor="mc-nom">Nom</Label>
            <Input id="mc-nom" value={profil.name} onChange={(e) => setProfil({ ...profil, name: e.target.value })} required className="rounded-none" data-testid="mon-compte-nom" />
          </div>
          <div>
            <Label>E-mail (identifiant de connexion)</Label>
            <p className="text-sm text-[#2C2C2C] px-3 py-2 bg-[#F5F5F3] border border-[#E0E0DB] break-all">{user?.email}</p>
          </div>
          <div>
            <Label>Rôle</Label>
            <p className="text-sm text-[#2C2C2C] px-3 py-2 bg-[#F5F5F3] border border-[#E0E0DB]">{ROLE_LABELS[user?.role] || user?.role}</p>
          </div>
          <div>
            <Label htmlFor="mc-tel">Téléphone</Label>
            <Input id="mc-tel" type="tel" value={profil.phone} onChange={(e) => setProfil({ ...profil, phone: e.target.value })} placeholder="06 12 34 56 78" className="rounded-none" data-testid="mon-compte-telephone" />
          </div>
          {/* Fluidité B2 (P1#26) — réglage repris de Paramètres › Mon profil (masqué aux non-admins) */}
          <label className="flex items-start gap-2 text-sm text-[#2C2C2C] cursor-pointer">
            <input type="checkbox" checked={profil.sms} onChange={(e) => setProfil({ ...profil, sms: e.target.checked })} className="mt-1" data-testid="mon-compte-sms" />
            <span>
              Recevoir les SMS urgents
              <span className="block text-xs text-[#6B6B6B]">Réserves urgentes, actions prioritaires, rappels de réunion (au numéro ci-dessus).</span>
            </span>
          </label>
          <Button type="submit" disabled={savingProfil} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none w-full sm:w-auto" data-testid="mon-compte-profil-save">
            {savingProfil ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </form>

        {prefsEmails?.concerne && (
          <div className="card-kroquis space-y-3 mb-6" data-testid="mon-compte-emails">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B] flex items-center gap-2">
              <EnvelopeSimple size={16} /> E-mails de chantier
            </h2>
            <p className="text-xs text-[#6B6B6B]">
              Les comptes rendus diffusés et les urgences (action urgente, échéance du jour ou dépassée, convocation) vous parviennent toujours immédiatement.
            </p>
            {[
              { value: 'condense', label: FONCTIONS.condenseQuotidien, aide: `Tout le reste regroupé en un seul e-mail par jour, vers ${prefsEmails.heure_envoi}.` },
              { value: 'immediat', label: FONCTIONS.emailsImmediats, aide: 'Un e-mail à chaque nouveauté.' },
            ].map((o) => (
              <label key={o.value} className={`flex items-start gap-3 p-3 border min-h-[44px] ${prefsEmails.frequence === o.value ? 'border-[#C4773B] bg-[#FDF6EC]' : 'border-[#E0E0DB]'} ${prefsEmails.modifiable ? 'cursor-pointer' : 'opacity-80'}`} data-testid={`mon-compte-emails-${o.value}`}>
                <input
                  type="radio"
                  name="frequence-emails"
                  className="mt-1 w-4 h-4"
                  checked={prefsEmails.frequence === o.value}
                  disabled={!prefsEmails.modifiable || savingPrefs}
                  onChange={() => choisirFrequence(o.value)}
                />
                <span>
                  <span className="block text-sm font-medium text-[#2C2C2C]">{o.label}</span>
                  <span className="block text-xs text-[#6B6B6B]">{o.aide}</span>
                </span>
              </label>
            ))}
            {!prefsEmails.modifiable && (
              <p className="text-xs text-[#6B6B6B]">Ce réglage est fait par le compte de votre entreprise{prefsEmails.entreprise_nom ? ` (${prefsEmails.entreprise_nom})` : ''}.</p>
            )}
            {!prefsEmails.condense_actif && (
              <p className="text-xs text-[#B8860B]" data-testid="mon-compte-emails-inactif">
                Le cabinet n'a pas encore activé le condensé quotidien : pour l'instant, vos e-mails arrivent au fil de l'eau.
              </p>
            )}
          </div>
        )}

        <form onSubmit={saveMdp} className="card-kroquis space-y-4" data-testid="mon-compte-mdp">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B] flex items-center gap-2">
            <Lock size={16} /> Changer mon mot de passe
          </h2>
          <div>
            <Label htmlFor="mc-mdp-actuel">Mot de passe actuel</Label>
            <Input id="mc-mdp-actuel" type="password" autoComplete="current-password" value={mdp.current} onChange={(e) => setMdp({ ...mdp, current: e.target.value })} required className="rounded-none" data-testid="mon-compte-mdp-actuel" />
          </div>
          <div>
            <Label htmlFor="mc-mdp-nouveau">Nouveau mot de passe</Label>
            <Input id="mc-mdp-nouveau" type="password" autoComplete="new-password" value={mdp.next} onChange={(e) => setMdp({ ...mdp, next: e.target.value })} placeholder={`${MIN_LENGTH} caractères minimum`} required className="rounded-none" data-testid="mon-compte-mdp-nouveau" />
          </div>
          <div>
            <Label htmlFor="mc-mdp-confirm">Confirmer le nouveau mot de passe</Label>
            <Input id="mc-mdp-confirm" type="password" autoComplete="new-password" value={mdp.confirm} onChange={(e) => setMdp({ ...mdp, confirm: e.target.value })} required className="rounded-none" data-testid="mon-compte-mdp-confirm" />
          </div>
          {erreurMdp && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200">
              <Warning size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{erreurMdp}</p>
            </div>
          )}
          <p className="text-xs text-[#6B6B6B]">
            Mot de passe actuel oublié ? Déconnectez-vous puis utilisez « Mot de passe oublié » sur l'écran de connexion.
          </p>
          <Button type="submit" disabled={savingMdp} className="bg-[#C75B39] hover:bg-[#A94B2E] rounded-none w-full sm:w-auto" data-testid="mon-compte-mdp-save">
            {savingMdp ? 'Modification…' : 'Changer mon mot de passe'}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
